import { useMemo, useEffect, useCallback, useState } from 'react'
import {
  getTimeFromSeconds,
  countCharacter,
  getSecondsFromTime,
  hideAllTooltips,
} from '@/utils/index'
import Button from '@/components/Button'
import ReactTooltip from 'react-tooltip'
import analytics from '@/utils/analytics'
import Input from '@/components/Input'
import Dropdown from '@/components/Dropdown'
import classNames from 'classnames'

export default function StartTimePicker({
  newAnnotationStart,
  setNewAnnotationStart,
  duration,
  isDemo,
  className,
}) {
  const durationTime = useMemo(() => getTimeFromSeconds(duration), [duration])
  const hasHours = useMemo(
    () => countCharacter(':', durationTime) === 2,
    [durationTime]
  )
  const [objectValue, setObjectValue] = useState(getObjectValue())
  const [isOpen, setIsOpen] = useState(false)

  // Reset value after we close the menu
  function onOpenChange(newIsOpen) {
    setIsOpen(newIsOpen)

    if (!newIsOpen) {
      const newObjectValue = getObjectValue()
      setObjectValue(newObjectValue)
      hideAllTooltips()
    }
  }

  useEffect(() => {
    const newObjectValue = getObjectValue()
    setObjectValue(newObjectValue)
  }, [newAnnotationStart])

  // '00:12:43' => { hours: '00', minutes: '12', seconds: '43' }
  function getObjectValue() {
    const time = getTimeFromSeconds(newAnnotationStart)
    const splitted = time.split(':')

    if (hasHours) {
      if (countCharacter(':', time) === 1) {
        const [minutes, seconds] = splitted
        return {
          hours: '00',
          minutes,
          seconds,
        }
      } else {
        const [hours, minutes, seconds] = splitted
        return {
          hours,
          minutes,
          seconds,
        }
      }
    }

    const [minutes, seconds] = splitted
    return {
      hours: '00',
      minutes,
      seconds,
    }
  }

  // { hours: '00', minutes: '12', seconds: '43' } => '00:12:43'
  function getStringValueFromObject() {
    const { hours, minutes, seconds } = objectValue

    if (hasHours) {
      return `${hours}:${minutes}:${seconds}`
    } else {
      return `${minutes}:${seconds}`
    }
  }

  function onStartTimeChange({ type, option }) {
    setObjectValue({
      ...objectValue,
      [type]: option,
    })
  }

  function onSubmit() {
    analytics.track(`${isDemo ? '(Demo) ' : ''}Start time changed`)

    const newValue = getStringValueFromObject()

    setTimeout(() => {
      const seconds = getSecondsFromTime(newValue)
      setNewAnnotationStart(+seconds)
    }, 0)
  }

  const value = getStringValueFromObject()

  const inputClassName = classNames(
    'cursor-pointer h-full text-center time-picker',
    {
      'ring-1 ring-primary border-primary': isOpen,
    }
  )

  const inputStyle = { minWidth: hasHours ? '98px' : '75px' }

  return (
    <div data-tip data-for="timepicker-tooltip" className={className}>
      <Dropdown
        className="ml-3 h-full"
        menuClassname="max-w-min"
        onOpenChange={onOpenChange}
        menuElement={
          <TimePickerMenu
            onChange={onStartTimeChange}
            hasHours={hasHours}
            durationTime={durationTime}
            onSubmit={onSubmit}
            objectValue={objectValue}
          />
        }
      >
        <Input
          type="text"
          value={value}
          className={inputClassName}
          style={inputStyle}
        />
      </Dropdown>

      <ReactTooltip
        place="top"
        effect="solid"
        id="timepicker-tooltip"
        clickable
      >
        <span className="center">Annotation start time</span>
      </ReactTooltip>
    </div>
  )
}

function TimePickerMenu({
  hasHours,
  onChange,
  durationTime,
  onSubmit,
  objectValue,
}) {
  const getHoursOptions = useCallback(() => {
    if (hasHours) {
      const hours = +durationTime.split(':')[0]
      return getOptions({ max: hours })
    } else {
      return []
    }
  }, [hasHours, durationTime])

  const getMinutesOptions = useCallback(() => {
    const minutes = +durationTime.split(':')[hasHours ? 1 : 0]

    if (!minutes) {
      return []
    }

    if (hasHours) {
      return getOptions({ max: 59 })
    } else {
      return getOptions({ max: minutes })
    }
  }, [hasHours, durationTime])

  const getSecondsOptions = useCallback(() => {
    return getOptions({ max: 59 })
  }, [hasHours, durationTime])

  const hoursOptions = getHoursOptions()
  const minutesOptions = getMinutesOptions()
  const secondsOptions = getSecondsOptions()

  return (
    <div className="flex flex-col">
      <div className="flex flex-1 max-h-36">
        {!!hoursOptions.length && (
          <TimeOptions
            onChange={onChange}
            options={hoursOptions}
            className=" border-gray-200"
            type="hours"
            objectValue={objectValue}
          />
        )}
        {!!minutesOptions.length && (
          <TimeOptions
            onChange={onChange}
            options={minutesOptions}
            className={hoursOptions.length ? ' border-gray-200 border-l' : ''}
            type="minutes"
            objectValue={objectValue}
          />
        )}
        {!!secondsOptions.length && (
          <TimeOptions
            onChange={onChange}
            options={secondsOptions}
            className=" border-gray-200 border-l"
            type="seconds"
            objectValue={objectValue}
          />
        )}
      </div>
      <div className="flex px-3 py-1 border-gray-200 border-t">
        <Button className="ml-auto !px-2 !py-1 -mb-1" onClick={onSubmit}>
          Ok
        </Button>
      </div>
    </div>
  )
}

function TimeOptions({ options, type, onChange, objectValue, className }) {
  const [selected, setSelected] = useState(objectValue[type])

  useEffect(() => {
    const newSelected = objectValue[type]
    setSelected(newSelected)
    const el = document.querySelector(`#${type}-${newSelected}`)

    if (el) {
      el.scrollIntoView({
        block: 'center',
        inline: 'center',
      })
    }
  }, [objectValue, type])

  return (
    <div
      className={`flex flex-col flex-1 overflow-y-scroll max-w-min ${className}`}
    >
      {options.map((option) => {
        const isSelected = selected === option
        const spanClassName = classNames('px-5 text-gray-700', {
          'bg-gray-100 pointer-events-none text-primary': isSelected,
        })

        return (
          <span
            id={`${type}-${option}`}
            className={'cursor-pointer center hover:bg-gray-200'}
            key={option}
            style={{ minHeight: '25px' }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onChange({ type, option })
            }}
          >
            <span className={spanClassName}>{option}</span>
          </span>
        )
      })}
    </div>
  )
}

function formatIndex(index) {
  if (index < 10) {
    return `0${index}`
  } else {
    return `${index}`
  }
}

function getOptions({ max }) {
  const options = []

  for (let index = 0; index <= max; index++) {
    options.push(formatIndex(index))
  }

  return options
}
